"use client"

import { useEffect, useState } from "react"
import { Shield, Lock, Eye, Database, Mail, Globe } from "lucide-react"

export default function PrivacyPage() {
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    setLoaded(true)
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="flex min-h-screen flex-col bg-[#1a1c1e] text-white">
      <main className="flex-1">
        {/* Hero Section */}
        <section
          className={`w-full py-16 md:py-24 bg-gradient-to-br from-[#1a1c1e] via-[#252729] to-[#1a1c1e] transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center space-y-6 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[#e57312]/10 backdrop-blur-sm border border-[#e57312]/20">
                <Shield className="h-10 w-10 text-[#e57312]" />
              </div>
              <div className="space-y-4">
                <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Privacy <span className="text-[#e57312]">Policy</span>
                </h1>
                <p className="max-w-[800px] text-xl text-gray-300">
                  Last Updated: February 2, 2026
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="w-full py-16 bg-[#1a1c1e]">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-4xl space-y-12">
              {/* Introduction */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Eye className="h-6 w-6 text-[#e57312]" />
                  <h2 className="text-3xl font-bold text-white">Introduction</h2>
                </div>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    At HSVE Consultation Services ("HSVE," "we," "our," or "us"), we are committed to protecting your
                    privacy and ensuring the security of your personal information. This Privacy Policy explains how we
                    collect, use, disclose, and safeguard your information when you visit our website, use our services,
                    or interact with us.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    By using our services, you consent to the data practices described in this policy. Please read this
                    Privacy Policy carefully to understand our views and practices regarding your personal data and how
                    we will treat it.
                  </p>
                </div>
              </div>

              {/* Information We Collect */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Database className="h-6 w-6 text-[#e57312]" />
                  <h2 className="text-3xl font-bold text-white">1. Information We Collect</h2>
                </div>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <h3 className="text-xl font-bold text-white">1.1 Personal Information</h3>
                  <p className="text-gray-300 leading-relaxed">
                    We may collect personal information that you voluntarily provide to us when you:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Register for an account</li>
                    <li>Purchase products or services</li>
                    <li>Contact us for consultation or support</li>
                    <li>Subscribe to our newsletter or communications</li>
                    <li>Participate in surveys or promotions</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed">
                    This information may include: name, email address, postal address, phone number, company name, job
                    title, payment information, and any other information you choose to provide.
                  </p>

                  <h3 className="text-xl font-bold text-white mt-6">1.2 Technical Information</h3>
                  <p className="text-gray-300 leading-relaxed">
                    When you use our website or services, we automatically collect certain information:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>IP address and location data</li>
                    <li>Browser type and version</li>
                    <li>Device information (operating system, device type, unique device identifiers)</li>
                    <li>Pages visited, time spent on pages, and navigation paths</li>
                    <li>Referring/exit pages and URLs</li>
                    <li>Date and time of access</li>
                  </ul>

                  <h3 className="text-xl font-bold text-white mt-6">1.3 License Usage Information</h3>
                  <p className="text-gray-300 leading-relaxed">
                    For licensed products, we may collect information about license validation, activation, usage
                    patterns, and technical performance to ensure proper functionality and prevent unauthorized use.
                  </p>

                  <h3 className="text-xl font-bold text-white mt-6">1.4 Cookies and Tracking Technologies</h3>
                  <p className="text-gray-300 leading-relaxed">
                    We use cookies, web beacons, and similar tracking technologies to collect information about your
                    browsing activities. You can control cookie settings through your browser preferences.
                  </p>
                </div>
              </div>

              {/* How We Use Your Information */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">2. How We Use Your Information</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">We use the information we collect to:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Provide, maintain, and improve our services</li>
                    <li>Process your transactions and send related information (confirmations, invoices, receipts)</li>
                    <li>Manage your account and provide customer support</li>
                    <li>Send administrative information, updates, and security alerts</li>
                    <li>Respond to your inquiries and fulfill your requests</li>
                    <li>Validate and manage product licenses</li>
                    <li>Personalize your experience and deliver relevant content</li>
                    <li>Send promotional communications (with your consent where required)</li>
                    <li>Analyze usage patterns and improve our website and services</li>
                    <li>Detect, prevent, and address technical issues, fraud, and security concerns</li>
                    <li>Comply with legal obligations and enforce our terms</li>
                  </ul>
                </div>
              </div>

              {/* Legal Basis for Processing */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">3. Legal Basis for Processing (GDPR)</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    If you are located in the European Economic Area (EEA) or United Kingdom, our legal basis for
                    processing your personal information includes:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>
                      <strong className="text-white">Contractual Necessity:</strong> Processing necessary to fulfill our
                      contract with you or to take steps at your request before entering into a contract
                    </li>
                    <li>
                      <strong className="text-white">Legitimate Interests:</strong> Processing necessary for our
                      legitimate business interests, such as improving our services, security, and fraud prevention
                    </li>
                    <li>
                      <strong className="text-white">Consent:</strong> Processing based on your explicit consent, which
                      you can withdraw at any time
                    </li>
                    <li>
                      <strong className="text-white">Legal Obligation:</strong> Processing necessary to comply with
                      legal requirements
                    </li>
                  </ul>
                </div>
              </div>

              {/* Information Sharing */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Globe className="h-6 w-6 text-[#e57312]" />
                  <h2 className="text-3xl font-bold text-white">4. How We Share Your Information</h2>
                </div>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    We do not sell, rent, or trade your personal information. We may share your information in the
                    following circumstances:
                  </p>

                  <h3 className="text-xl font-bold text-white mt-4">4.1 Service Providers</h3>
                  <p className="text-gray-300 leading-relaxed">
                    We may share information with third-party service providers who perform services on our behalf, such
                    as:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Payment processing (e.g., Stripe)</li>
                    <li>Cloud hosting and storage</li>
                    <li>Email delivery and communications</li>
                    <li>Analytics and performance monitoring</li>
                    <li>Customer support tools</li>
                  </ul>

                  <h3 className="text-xl font-bold text-white mt-6">4.2 Legal Requirements</h3>
                  <p className="text-gray-300 leading-relaxed">
                    We may disclose your information if required by law or in response to legal processes, such as
                    subpoenas, court orders, or regulatory requests.
                  </p>

                  <h3 className="text-xl font-bold text-white mt-6">4.3 Business Transfers</h3>
                  <p className="text-gray-300 leading-relaxed">
                    In the event of a merger, acquisition, reorganization, or sale of assets, your information may be
                    transferred as part of the transaction. We will notify you of any such change.
                  </p>

                  <h3 className="text-xl font-bold text-white mt-6">4.4 Protection and Safety</h3>
                  <p className="text-gray-300 leading-relaxed">
                    We may share information when we believe it is necessary to protect our rights, property, or safety,
                    or the rights, property, or safety of others.
                  </p>

                  <h3 className="text-xl font-bold text-white mt-6">4.5 With Your Consent</h3>
                  <p className="text-gray-300 leading-relaxed">
                    We may share your information for any other purpose with your explicit consent.
                  </p>
                </div>
              </div>

              {/* Data Security */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Lock className="h-6 w-6 text-[#e57312]" />
                  <h2 className="text-3xl font-bold text-white">5. Data Security</h2>
                </div>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    We implement appropriate technical and organizational security measures to protect your personal
                    information against unauthorized access, alteration, disclosure, or destruction. These measures
                    include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Encryption of data in transit and at rest</li>
                    <li>Secure hosting infrastructure</li>
                    <li>Regular security assessments and monitoring</li>
                    <li>Access controls and authentication mechanisms</li>
                    <li>Employee training on data protection</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed">
                    However, no method of transmission over the Internet or electronic storage is 100% secure. While we
                    strive to protect your information, we cannot guarantee absolute security.
                  </p>
                </div>
              </div>

              {/* Data Retention */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">6. Data Retention</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    We retain your personal information for as long as necessary to fulfill the purposes outlined in
                    this Privacy Policy, unless a longer retention period is required or permitted by law. Factors we
                    consider when determining retention periods include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>The duration of our relationship with you</li>
                    <li>Legal or regulatory requirements</li>
                    <li>Dispute resolution needs</li>
                    <li>Business operational requirements</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed">
                    When we no longer need your information, we will securely delete or anonymize it.
                  </p>
                </div>
              </div>

              {/* Your Rights */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">7. Your Rights and Choices</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    Depending on your location, you may have certain rights regarding your personal information:
                  </p>

                  <h3 className="text-xl font-bold text-white mt-4">7.1 Access and Portability</h3>
                  <p className="text-gray-300 leading-relaxed">
                    You have the right to request access to your personal information and receive a copy in a structured,
                    commonly used format.
                  </p>

                  <h3 className="text-xl font-bold text-white mt-6">7.2 Correction</h3>
                  <p className="text-gray-300 leading-relaxed">
                    You can request that we correct inaccurate or incomplete personal information.
                  </p>

                  <h3 className="text-xl font-bold text-white mt-6">7.3 Deletion</h3>
                  <p className="text-gray-300 leading-relaxed">
                    You can request deletion of your personal information, subject to certain legal exceptions.
                  </p>

                  <h3 className="text-xl font-bold text-white mt-6">7.4 Restriction and Objection</h3>
                  <p className="text-gray-300 leading-relaxed">
                    You can request restriction of processing or object to processing of your information in certain
                    circumstances.
                  </p>

                  <h3 className="text-xl font-bold text-white mt-6">7.5 Withdraw Consent</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Where processing is based on consent, you can withdraw your consent at any time.
                  </p>

                  <h3 className="text-xl font-bold text-white mt-6">7.6 Marketing Communications</h3>
                  <p className="text-gray-300 leading-relaxed">
                    You can opt out of receiving marketing communications by following the unsubscribe instructions in
                    our emails or contacting us directly.
                  </p>

                  <p className="text-gray-300 leading-relaxed mt-6">
                    To exercise any of these rights, please contact us at support@hsve.cc. We will respond to your
                    request within the timeframe required by applicable law.
                  </p>
                </div>
              </div>

              {/* International Data Transfers */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">8. International Data Transfers</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    Your information may be transferred to and processed in countries other than your country of
                    residence. These countries may have data protection laws that differ from those in your country.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    When we transfer information outside the EEA or UK, we ensure appropriate safeguards are in place,
                    such as standard contractual clauses approved by the European Commission or other legally recognized
                    transfer mechanisms.
                  </p>
                </div>
              </div>

              {/* Children's Privacy */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">9. Children's Privacy</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    Our services are not directed to individuals under the age of 18. We do not knowingly collect
                    personal information from children. If we become aware that we have collected information from a
                    child without parental consent, we will take steps to delete that information.
                  </p>
                </div>
              </div>

              {/* Third-Party Links */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">10. Third-Party Links and Services</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    Our website may contain links to third-party websites or services that are not owned or controlled
                    by HSVE. We are not responsible for the privacy practices of these third parties. We encourage you
                    to review the privacy policies of any third-party sites you visit.
                  </p>
                </div>
              </div>

              {/* Changes to Privacy Policy */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">11. Changes to This Privacy Policy</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    We may update this Privacy Policy from time to time to reflect changes in our practices, technology,
                    legal requirements, or other factors. We will post the updated policy on this page and update the
                    "Last Updated" date.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    For material changes, we will provide additional notice, such as by email or through a prominent
                    notice on our website. We encourage you to review this Privacy Policy periodically.
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-6 w-6 text-[#e57312]" />
                  <h2 className="text-3xl font-bold text-white">12. Contact Us</h2>
                </div>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    If you have any questions, concerns, or requests regarding this Privacy Policy or our data
                    practices, please contact us:
                  </p>
                  <div className="space-y-2 text-gray-300">
                    <p>
                      <strong className="text-white">HSVE Consultation Services</strong>
                    </p>
                    <p>Email: support@hsve.cc</p>
                    <p>Website: https://hsve.cc</p>
                  </div>
                  <p className="text-gray-300 leading-relaxed mt-4">
                    If you are located in the EEA or UK and have concerns about our data processing practices, you have
                    the right to lodge a complaint with your local data protection authority.
                  </p>
                </div>
              </div>

              {/* Acknowledgment */}
              <div className="rounded-xl border-2 border-[#e57312]/50 bg-gradient-to-br from-[#e57312]/10 to-[#252729]/50 p-8 space-y-4">
                <h3 className="text-2xl font-bold text-[#e57312]">Your Privacy Matters</h3>
                <p className="text-gray-300 leading-relaxed">
                  At HSVE, we take your privacy seriously and are committed to transparency in how we handle your
                  personal information. We continuously review and improve our data protection practices to ensure your
                  information remains secure.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  BY USING OUR SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THIS PRIVACY POLICY.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
